import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, LogOut, Mail, BookOpen, Inbox, Send, UserPlus, Shield, Kanban, BarChart3, Target, Calculator } from "lucide-react";
import { clearAuth, getUser, testEmail } from "@/lib/admin-api";
import { toast } from "sonner";
import { useState } from "react";

export default function AdminLayout() {
  const nav = useNavigate();
  const user = getUser();
  const [testing, setTesting] = useState(false);

  const logout = () => {
    clearAuth();
    nav("/admin/login");
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await testEmail();
      if (r.result?.status === "sent") toast.success(`Email de prueba enviado. Revisa tu bandeja.`);
      else if (r.result?.status === "skipped") toast.error("Resend no configurado. Agrega RESEND_API_KEY en .env");
      else toast.error(`Fallo: ${r.result?.error || "desconocido"}`);
    } catch (e) {
      toast.error("Error enviando email");
    } finally {
      setTesting(false);
    }
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
      isActive ? "bg-red-600 text-white" : "text-neutral-700 hover:bg-neutral-100"
    }`;

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen bg-white border-r border-neutral-200 flex flex-col">
        <div className="p-5 border-b border-neutral-200 flex items-center gap-3">
          <img src="/images/logo_eclipse_oficial.png" alt="Eclipse" className="h-9 w-auto" />
          <div>
            <p className="font-black text-sm tracking-wider">ECLIPSE</p>
            <p className="text-xs text-neutral-500 -mt-0.5">Admin FlexShade</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <NavLink to="/admin" end className={linkClass}>
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/admin/pipeline" className={linkClass} data-testid="nav-pipeline">
            <Kanban className="w-4 h-4" /> Pipeline
          </NavLink>
          <NavLink to="/admin/leads" className={linkClass}>
            <Users className="w-4 h-4" /> Leads
          </NavLink>
          <NavLink to="/admin/analytics" className={linkClass} data-testid="nav-analytics">
            <BarChart3 className="w-4 h-4" /> Analítica
          </NavLink>
          <NavLink to="/admin/calculadora" className={linkClass} data-testid="nav-calculadora">
            <Calculator className="w-4 h-4" /> Calculadora
          </NavLink>
          <NavLink to="/admin/ads-setup" className={linkClass} data-testid="nav-ads-setup">
            <Target className="w-4 h-4" /> Google Ads
          </NavLink>
          <NavLink to="/admin/emails" className={linkClass}>
            <Inbox className="w-4 h-4" /> Registro correos
          </NavLink>
          <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            Email marketing
          </div>
          <NavLink to="/admin/subscribers" className={linkClass} data-testid="nav-subscribers">
            <UserPlus className="w-4 h-4" /> Suscriptores
          </NavLink>
          <NavLink to="/admin/campaigns" className={linkClass} data-testid="nav-campaigns">
            <Send className="w-4 h-4" /> Campañas
          </NavLink>
          <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            Recursos
          </div>
          <NavLink to="/admin/manual" className={linkClass}>
            <BookOpen className="w-4 h-4" /> Manual
          </NavLink>
          <NavLink to="/admin/diagnostic" className={linkClass} data-testid="nav-diagnostic">
            <Shield className="w-4 h-4" /> Diagnóstico
          </NavLink>
        </nav>
        <div className="p-3 border-t border-neutral-200 space-y-2 pb-20 lg:pb-3">
          <button
            onClick={sendTest}
            disabled={testing}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-neutral-600 hover:bg-neutral-100 rounded-lg disabled:opacity-60"
            data-testid="test-email-btn"
          >
            <Mail className="w-4 h-4" /> {testing ? "Enviando..." : "Probar email"}
          </button>
          <div className="px-3 py-2 text-xs text-neutral-500 border-t border-neutral-100 pt-2">
            <p className="font-semibold text-neutral-700 truncate">{user?.nombre}</p>
            <p className="truncate">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="relative z-10 w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
